/**
 * Roadmap.jsx
 *
 * Four-phase timeline down a glowing spine, with a light that travels
 * its length and phases that reveal as they scroll into view.
 *
 * Purely presentational — nothing here reads chain state, so it stays
 * accurate whatever the sale is doing. Phase status is set by hand in
 * PHASES below; update `status` as milestones land.
 */
import React, { useEffect, useRef, useState } from 'react';
import Panel from '../Panel';
import GlowText from '../GlowText';

const PHASES = [
  {
    n: '01',
    title: 'FOUNDATION',
    status: 'done',
    items: [
      'Launch HEXABOTS brand & lore',
      'Introduce the HEXABOTS characters',
      'Launch website & build the community',
      'Build the WL game & task system',
      'Start partnerships',
    ],
  },
  {
    n: '02',
    title: 'PREPARE FOR LAUNCH',
    status: 'active',
    items: [
      'Introduce mint details',
      'Publish the roadmap',
      'NFT collection launch',
    ],
  },
  {
    n: '03',
    title: 'COMMUNITY & GAMEPLAY',
    status: 'next',
    items: [
      'Holder-gated socials',
      'Holder-exclusive giveaways',
      'HEXA mini-games & mining',
      'Mine $HEXA through your NFTs',
      'Every NFT carries its own hash rate',
      '$HEXA token utility',
      'Public beta of the game',
      'Community playtesting',
      'Leaderboards & achievements',
      'Earn $HEXA through gameplay',
      'Player rewards & events',
      'Balance and improve the game',
    ],
  },
  {
    n: '04',
    title: 'TOKEN, GAME & MINING',
    status: 'next',
    items: [
      'Burning mechanism',
      'Repair & upgrade system',
      '$HEXA presale',
      'Official $HEXA token launch',
      'Mining mechanics',
      'And more…',
    ],
  },
];

const STATUS_META = {
  done:   { label: 'COMPLETE',    cls: 'ok-green', pill: 'live' },
  active: { label: 'IN PROGRESS', cls: 'cyan',     pill: 'live' },
  next:   { label: 'UPCOMING',    cls: 'dim',      pill: 'upcoming' },
};

/** Reveals children once they scroll into view. */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    // No IntersectionObserver (old browser, or a test env)? Show it
    // rather than leaving the section permanently invisible.
    if (!el || typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
}

function StatusPill({ status }) {
  const meta = STATUS_META[status];
  const colour = status === 'done' ? 'var(--ok-green)'
               : status === 'active' ? 'var(--cyan)' : 'var(--text-dim)';
  return (
    <span
      className="font-display shrink-0"
      style={{
        fontSize: 9.5, fontWeight: 800, letterSpacing: '.12em',
        padding: '4px 10px',
        color: status === 'active' ? '#05070c' : colour,
        background: status === 'active' ? 'var(--cyan)' : 'rgba(10,15,28,.65)',
        border: status === 'active' ? 'none' : `1px solid ${colour}`,
        clipPath: 'polygon(6px 0,100% 0,100% calc(100% - 6px),calc(100% - 6px) 100%,0 100%,0 6px)',
        boxShadow: status === 'active' ? '0 0 16px rgba(0,229,255,.55)' : 'none',
      }}
    >
      {meta.label}
    </span>
  );
}

function Phase({ phase, index }) {
  const [ref, seen] = useInView();
  const done = phase.status === 'done';
  const active = phase.status === 'active';
  const accent = done ? 'var(--ok-green)' : active ? 'var(--cyan)' : 'var(--steel-light)';

  return (
    <div
      ref={ref}
      className={`flex gap-4 ${seen ? 'rm-phase' : ''}`}
      style={{
        opacity: seen ? undefined : 0,
        animationDelay: `${index * 110}ms`,
        paddingBottom: 26,
      }}
    >
      {/* Node */}
      <div className={`rm-node ${phase.status}`}>
        {done ? '✓' : phase.n}
      </div>

      {/* Card */}
      <div
        className="mech-panel rm-card flex-1"
        style={{
          padding: '16px 18px',
          borderColor: active ? 'var(--cyan)' : 'var(--steel)',
          minWidth: 0,
        }}
      >
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap" style={{ rowGap: 8 }}>
          <div className="flex items-baseline gap-2.5 min-w-0">
            <span
              className="font-mono-hud shrink-0"
              style={{ fontSize: 11, color: accent, letterSpacing: '.1em' }}
            >
              PHASE {phase.n}
            </span>
            <GlowText
              text={phase.title}
              size={14}
              variant={done ? 'cyan' : active ? 'cyan' : 'white'}
            />
          </div>
          <StatusPill status={phase.status} />
        </div>

        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {phase.items.map((item, i) => (
            <li
              key={item}
              className={`flex items-start gap-2.5 ${seen ? 'rm-item' : ''}`}
              style={{
                opacity: seen ? undefined : 0,
                animationDelay: `${index * 110 + 140 + i * 45}ms`,
                padding: '6px 0',
              }}
            >
              <span
                className="rm-item-dot"
                style={{
                  borderColor: done ? 'var(--ok-green)' : accent,
                  color: done ? 'var(--ok-green)' : accent,
                  background: done ? 'rgba(18,217,122,.14)' : 'transparent',
                }}
              >
                {done ? '✓' : ''}
              </span>
              <span
                className="font-mono-hud"
                style={{
                  fontSize: 'clamp(12px,1.9vw,13.5px)',
                  color: 'var(--text)',
                  lineHeight: 1.5,
                }}
              >
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function Roadmap() {
  return (
    <Panel title="ROADMAP">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <GlowText as="div" text="THE PATH AHEAD" size={17} variant="cyan" />
        <span className="font-mono-hud text-dim text-fluid-sm">
          Four phases, from lore to a live token economy.
        </span>
      </div>

      <div style={{ position: 'relative' }}>
        {/* Spine sits behind the nodes and links them together */}
        <div className="rm-spine" aria-hidden="true">
          <span className="rm-spark" />
        </div>

        {PHASES.map((p, i) => <Phase key={p.n} phase={p} index={i} />)}
      </div>
    </Panel>
  );
}
